import { useEffect, useState } from 'react';
import { EntryForm, Column, Input, Select } from '../components/EntryForm';
import Page from '../components/Page';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { validations } from '../lib/form';
import responseProcessor from '../lib/responseProcessor';

// Import providers and services
import { useUserProvider } from '../providers/UserProvider';
import { useModalProvider } from '../providers/ModalProvider';
import { useLoaderProvider } from '../providers/LoaderProvider';
import { GetCustomCTA, UpdateCustomCTA, AddCustomCTA } from '../services/api/custom_cta';
import DataSelector from '../components/DataSelector';
import { Radio, } from 'antd';
import { contentList } from '../components/modal_contents/PushNotificationModal/contentList';
import VersionizerWrapper from '../components/Versionizer';
import { IsLowerVersion } from '../lib/useVersionizer';

const CustomCTADetail = ({ match }) => {
    const initialVals = {
        name: '',
        cta_type: 'URL CTA',
        text: '',
        url: '',
        content_type: '',
        content: '',
        tag: ''
    };

    const [initialValues, setInitialValues] = useState(initialVals);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedContentType, setSelectedContentType] = useState('');


    const { user } = useUserProvider();
    const { showModal } = useModalProvider();
    const { doSetShowLoadingModal } = useLoaderProvider();
    const history = useHistory();
    const shouldHideCta = IsLowerVersion('10.0')

    const uuid = match?.params?.uuid;
    const newCTA = uuid === 'new';

    const loadData = () => {
        setIsLoading(true);
        GetCustomCTA(uuid, user.idToken)
            .then((res) => res.json())
            .then((res) => {

                const data = res.data;
                setSelectedContentType(data?.content?.type?.toLowerCase() ?? '');
                setInitialValues({
                    name: data.name,
                    cta_type: data?.name?.toLowerCase() === "register now" ? "Register Now" : data.cta_type,
                    text: data.text,
                    url: data.cta_type === 'URL CTA' ? data.url : '',
                    content_type: data.cta_type === 'Content CTA' ? data?.content?.type?.toLowerCase() : '',
                    content: data.content.id,
                    tag: data?.tag
                });


                setIsLoading(false);
            })
            .catch((error) => {
                console.error('Error loading CTA:', error);
                showModal({
                    type: 'error',
                    message: 'Failed to load Custom CTA data'
                });
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!newCTA) {
            loadData();
        } else {
            setInitialValues(initialVals);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newCTA, uuid]);

    useEffect(() => {
        doSetShowLoadingModal(isLoading);
    }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const currentAction = newCTA ? 'New' : 'Edit';
    const currentUrl = newCTA ? '/custom_cta/new' : `/custom_cta/${uuid}`;

    const links = [
        { name: 'Home', url: '/' },
        { name: 'Preset CTA', url: '/custom_cta' },
        { name: `${currentAction} Preset CTA`, url: currentUrl },
    ];

    const schema = {
        name: validations.str_req_45,
        cta_type: Yup.string().required('CTA Type is required'),
        text: validations.str_req_45,
        url: Yup.string().when('cta_type', {
            is: 'URL CTA',
            then: Yup.string().required('URL is required').url('Must be a valid URL'),
        }),
        content_type: Yup.string().when('cta_type', {
            is: 'Content CTA',
            then: Yup.string().required('Content Type is required'),
        }),
        content: Yup.string().when('cta_type', {
            is: 'Content CTA',
            then: Yup.string().required('Content must be selected'),
        }),
        tag: Yup.string().when('cta_type', {
            is: 'Register Now',
            then: Yup.string().required('A tag must be selected'),
        }),
    };

    const contentListToDisplay = IsLowerVersion('10.0') ? contentList : [...contentList, {
        name: 'Flex Content',
        value: 'flex',
        resourceEndpoint: '/content/flexes?push=true'
      }];


    const responseProcessorProxy = (response) => {
        responseProcessor({
            response,
            showModal,
            onSuccess: () => history.push('/custom_cta'),
        });
    };

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: Yup.object().shape(schema),
        onSubmit: async (values) => {
            const payload = {
                name: values.name,
                cta_type: values.cta_type,
                text: values.text,
                tag: values.tag
            };

            if (values.cta_type === 'URL CTA') {
                payload.url = values.url;
            } else if (values.cta_type === 'Content CTA') {
                payload.content = {
                    type: values.content_type,
                    id: values.content,
                }
            } else if (values.cta_type === 'Register Now') {
                payload.content = null;
                payload.content_type = null;
                payload.url = null;
            }

            if (newCTA) {
                AddCustomCTA(user.idToken, { data: payload }, user.currentApp()).then(
                    (response) => responseProcessorProxy(response)
                );
            } else {
                UpdateCustomCTA(user.idToken, {
                    data: payload,
                }, uuid).then((response) => responseProcessorProxy(response));
            }
        },
    });

    return (
        <Page links={links} title={`${currentAction} PRESET CTA`}>
            <EntryForm
                isLoading={isLoading}
                formik={formik}
                submitHandler={formik.handleSubmit}
                cancelURL="/custom_cta"
            >
                <Column>
                    <Input
                        name="name"
                        type="text"
                        label="Name"
                        formik={formik}
                        disabled={formik.values.name?.toLowerCase() === "register now"}
                    />
                    {
                        shouldHideCta ? (
                            <></>
                        ) : (
                            <>
                                <Radio.Group
                                    formik={formik}
                                    className="flex flex-row w-2/3 "
                                    value={formik?.values?.cta_type ?? ""}
                                    onChange={(e) => {
                                        formik.setFieldValue('cta_type', e.target.value);
                                        // Reset fields when changing CTA type
                                        if (e.target.value === 'URL CTA') {
                                            formik.setFieldValue('content_type', '');
                                            formik.setFieldValue('content', '');
                                            formik.setFieldTouched('content', false);
                                            formik.setFieldTouched('content_type', false);
                                            formik.setFieldError('content', undefined);
                                            formik.setFieldError('content_type', undefined);
                                            setSelectedContentType('');
                                        } else {
                                            formik.setFieldValue('url', '');
                                        }
                                    }}
                                >
                                    <Radio value="URL CTA" disabled={formik?.values?.cta_type === "Register Now"}>URL CTA</Radio>
                                    <VersionizerWrapper minVersion={10}>
                                        <Radio value="Content CTA" disabled={formik?.values?.cta_type === "Register Now"}>Content CTA</Radio>
                                        { formik?.values?.cta_type === "Register Now" && <Radio value="Register Now">Register CTA</Radio> }
                                    </VersionizerWrapper>
                                </Radio.Group>

                            </>
                        )
                    }
                    <div className='mt-5'>
                        <Input
                            name="text"
                            type="text"
                            label="Call to Action Text"
                            formik={formik}
                        />
                    </div>
                    {formik.values.cta_type === 'URL CTA' && (
                        <Input
                            name="url"
                            type="text"
                            label="Call to Action URL"
                            formik={formik}
                        />
                    )}

                    {formik.values.cta_type === 'Content CTA' && (
                        <div className='mb-5'>
                            <div className='mt-5'>
                                Content Type
                            </div>
                            <Select
                                className='w-full'
                                onChange={(e) => {
                                    formik.setFieldValue('content', '');
                                    formik.setFieldTouched('content', false);
                                    formik.setFieldError('content', undefined);
                                    setSelectedContentType(e.target.value);
                                }}
                                value={formik.values.content_type}
                                name="content_type"
                                formik={formik}
                            >
                                <option value="">- Content -</option>
                                {contentListToDisplay.map((item) => (
                                    <option
                                        key={item.value}
                                        value={item.value}
                                        label={item.name}
                                    />
                                ))}
                            </Select>
                            {selectedContentType && (
                                <div className='mb-3 mt-5'>
                                    <DataSelector
                                        label="Content"
                                        name="content"
                                        formik={formik}
                                        singleSelectOnly={true}
                                        resourceEndpoint={`${contentListToDisplay.find(item =>
                                            item.value === selectedContentType)?.resourceEndpoint}`}
                                        // AE-4795: Removed frontend active filter - filtering now handled by API
                                        // Previous implementation filtered by active status on frontend, causing
                                        // inactive resources to not appear. API now handles this filtering.
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {formik.values.cta_type === 'Register Now' && (
                        <DataSelector
                            name="tag"
                            formik={formik}
                            label="Tags"
                            resourceEndpoint="tags"
                            filterOptions={(values) => values.filter((a) => !a.is_expired)}
                            singleSelectOnly
                            noMarginBottom
                            labelClassName="-mb-0"
                        />
                    )}
                </Column>
            </EntryForm>
        </Page>
    );
};

export default CustomCTADetail;